import {
  Badge,
  Box,
  Circle,
  Flex,
  Grid,
  HStack,
  Input,
  Separator,
  Skeleton,
  Spinner,
  Stack,
  Tag,
  Text,
} from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import {
  SessionEndedBadge,
  SessionLiveBadge,
  SessionMemberAvatars,
  SessionSeatsCount,
} from '#modules/finder/features/posts/components/SessionParts';
import { Placeholder } from '#modules/user/features/dashboard';
import {
  AddIcon,
  Button,
  CalendarTimeIcon,
  Chip,
  Link,
  ListEmpty,
  MicOffIcon,
  MicOnIcon,
  Panel,
  SectionHeading,
} from '#features/ui';
import { CloseButton } from '#features/ui/components/CloseButton';
import { GamePresence } from '#features/ui/components/GamePresence/GamePresence';
import { IconButton } from '#features/ui/components/IconButton';
import { PersonRow } from '#features/ui/components/PersonRow';
import { CheckboxCard } from '#features/ui/components/CheckboxCard';
import { ClipboardInput, ClipboardRoot } from '#features/ui/components/Clipboard';
import { Field } from '#features/ui/components/Field';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '#features/ui/components/Menu';
import { OnlinePresence } from '#features/ui/components/OnlinePresence';
import { RadioCardItem, RadioCardRoot } from '#features/ui/components/RadioCard';
import { Avatar } from '#features/ui/components/Avatar';
import {
  DIALOG_ACCENT_BRAND,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogHeaderIcon,
  DialogRoot,
} from '#features/ui/components/Dialog';

// ── Data ─────────────────────────────────────────────────────────────

const textStyles = [
  { name: 'heading', desc: 'xl / 700', sample: 'Page Title', uses: 17 },
  { name: 'subtitle', desc: 'xl / 600', sample: 'Dialog Header', uses: 22 },
  { name: 'section', desc: 'md / 600', sample: 'Card Title', uses: 10 },
  { name: 'nav', desc: 'md / 500', sample: 'Navigation Item', uses: 5 },
  { name: 'strong', desc: 'sm / 700', sample: '12.4 K/D', uses: 16 },
  { name: 'title', desc: 'sm / 600', sample: 'Section Heading', uses: 127 },
  { name: 'body', desc: 'sm / 400', sample: 'Body text description goes here', uses: 66 },
  { name: 'label', desc: 'xs / 600', sample: 'Rank', uses: 45 },
  { name: 'caption', desc: 'xs / 500', sample: '2h · 3 members · Mar 26', uses: 157 },
  { name: 'badge', desc: 'xs / 700', sample: 'LIVE', uses: 45 },
  { name: 'micro', desc: '2xs / 700', sample: '99', uses: 105 },
] as const;

const bgTokens = [
  { name: 'bg', token: 'bg', value: 'brand.900', desc: 'Page background', uses: 4 },
  {
    name: 'bg.surface',
    token: 'bg.surface',
    value: 'brand.800',
    desc: 'Surfaces, sidebars, headers',
    uses: 13,
  },
  {
    name: 'bg.card',
    token: 'bg.card',
    value: 'brand.700',
    desc: 'Cards, elevated panels',
    uses: 22,
  },
  {
    name: 'bg.emphasized',
    token: 'bg.emphasized',
    value: 'brand.600',
    desc: 'Emphasized sections',
    uses: 17,
  },
  {
    name: 'bg.muted',
    token: 'bg.muted',
    value: 'whiteAlpha.100',
    desc: 'Pills, tags, hover, fills',
    uses: 65,
  },
  {
    name: 'bg.overlay',
    token: 'bg.overlay',
    value: 'hsla(228,11.6%,20%,0.5)',
    desc: 'Modal overlays',
    uses: 6,
  },
] as const;

const fgTokens = [
  { name: 'fg', value: 'whiteAlpha.900', desc: 'Primary text', uses: 14 },
  { name: 'fg.emphasized', value: 'whiteAlpha.800', desc: 'Emphasized text, names', uses: 48 },
  { name: 'fg.secondary', value: 'whiteAlpha.700', desc: 'Secondary text, icons', uses: 37 },
  { name: 'fg.muted', value: 'whiteAlpha.500', desc: 'Muted text, meta info', uses: 87 },
  { name: 'fg.subtle', value: 'whiteAlpha.300', desc: 'Separators (·), hints', uses: 31 },
  { name: 'fg.error', value: 'red.400', desc: 'Errors, delete actions', uses: 4 },
  { name: 'fg.warning', value: 'orange.200', desc: 'Warnings, blocked', uses: 6 },
  { name: 'fg.success', value: 'green.400', desc: 'Live, active, online', uses: 8 },
  { name: 'fg.info', value: 'blue.300', desc: 'Upcoming, scheduled', uses: 1 },
  { name: 'fg.link', value: 'teal.300', desc: 'Links, navigation', uses: 5 },
  { name: 'fg.link.hover', value: 'teal.200', desc: 'Link hover / edit', uses: 3 },
] as const;

const borderTokens = [
  {
    name: 'border',
    value: 'whiteAlpha.200',
    desc: 'Default borders, hover, outline buttons',
    uses: 8,
  },
  { name: 'border.subtle', value: 'whiteAlpha.100', desc: 'Separators, faint lines', uses: 14 },
] as const;

// ── Components ───────────────────────────────────────────────────────

const UseBadge = ({ count }: { count: number }) => (
  <Badge
    bg={
      count > 30
        ? 'green.500/20'
        : count > 10
          ? 'blue.500/15'
          : count > 0
            ? 'whiteAlpha.100'
            : 'red.500/15'
    }
    color={count > 30 ? 'fg.success' : count > 10 ? 'fg.info' : count > 0 ? 'fg.muted' : 'fg.error'}
    borderRadius="full"
    px={2}
    textStyle="micro"
    variant="plain"
  >
    {count}×
  </Badge>
);

const SectionTitle = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Stack gap={4}>
    <Text textStyle="heading" color="fg">
      {title}
    </Text>
    <Separator borderColor="border.subtle" />
    {children}
  </Stack>
);

const DialogPreview = ({
  label,
  title,
  icon,
  description,
  accent,
}: {
  label: string;
  title: string;
  icon?: boolean;
  description?: string;
  accent?: boolean;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        {label}
      </Button>
      <DialogRoot open={open} onOpenChange={(e) => setOpen(e.open)} size="md">
        <DialogContent>
          <DialogHeader
            title={title}
            description={description}
            icon={icon ? <DialogHeaderIcon src="/android-chrome-192x192.png" alt="" /> : undefined}
            accent={accent ? DIALOG_ACCENT_BRAND : undefined}
          />
          <DialogBody>
            <Text textStyle="body" color="fg.secondary">
              Содержимое модалки.
            </Text>
          </DialogBody>
        </DialogContent>
      </DialogRoot>
    </>
  );
};

// Демонстрация задержки: спиннер появляется только если ответ не пришёл за DELAY.
// Тот же приём, что в Grid и DiscoverList — быстрый запрос не мигает крутилкой.
const DELAY = 500;

const DelayedSpinnerDemo = ({ latency, label }: { latency: number; label: string }) => {
  const [runId, setRunId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    if (!runId) return;

    setLoading(true);
    setShowSpinner(false);

    const spinnerTimer = setTimeout(() => setShowSpinner(true), DELAY);
    const doneTimer = setTimeout(() => setLoading(false), latency);

    return () => {
      clearTimeout(spinnerTimer);
      clearTimeout(doneTimer);
    };
  }, [runId, latency]);

  return (
    <Stack gap={2} w="220px">
      <Text textStyle="caption" color="fg.muted">
        {label}
      </Text>
      <Flex
        h="72px"
        alignItems="center"
        justifyContent="center"
        bg="bg.card"
        borderRadius="lg"
        borderWidth="1px"
        borderColor="border.subtle"
      >
        {loading ? (
          showSpinner ? (
            <Spinner borderWidth="3px" animationDuration="0.85s" size="lg" />
          ) : null
        ) : (
          <Text textStyle="caption" color="fg.muted">
            {runId ? 'Загружено' : '–'}
          </Text>
        )}
      </Flex>
      <Button size="sm" variant="outline" onClick={() => setRunId((n) => n + 1)}>
        Загрузить
      </Button>
    </Stack>
  );
};

// Скелетон списка чатов — копия ChatListSkeleton.
const ChatSkeletonPreview = () => (
  <Stack gap={0} px={1.5} py={1.5} w="280px">
    {['100px', '70px', '120px'].map((width, index) => (
      <HStack key={index} gap={3} px={3} py={2.5} alignItems="center">
        <Skeleton borderRadius="full" boxSize="40px" flexShrink={0} />
        <Stack gap={2} flex={1} minW={0}>
          <HStack justifyContent="space-between">
            <Skeleton h="12px" w={width} borderRadius="md" />
            <Skeleton h="8px" w="40px" borderRadius="md" />
          </HStack>
          <Skeleton h="10px" w="80%" borderRadius="md" />
        </Stack>
      </HStack>
    ))}
  </Stack>
);

// Skeleton loading={} оборачивает готовую разметку — размер берётся у содержимого.
const WrapSkeletonDemo = () => {
  const [ready, setReady] = useState(false);

  return (
    <Stack gap={2} w="280px">
      <Skeleton loading={!ready} borderRadius="md">
        <Box
          bgColor="bg.card"
          borderRadius="md"
          p={3}
          borderWidth="1px"
          borderColor="border.subtle"
        >
          <Text textStyle="body" color="fg.secondary" wordBreak="break-all">
            {ready ? 'teamplay.gg/i/9fK2xQ' : ' '}
          </Text>
        </Box>
      </Skeleton>
      <Button size="sm" variant="outline" onClick={() => setReady((v) => !v)}>
        {ready ? 'Сбросить' : 'Показать данные'}
      </Button>
    </Stack>
  );
};

// Кнопка сама держит своё состояние отправки — отдельный спиннер рядом не нужен.
const LoadingButtonDemo = () => {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!busy) return;

    const timer = setTimeout(() => setBusy(false), 1600);

    return () => clearTimeout(timer);
  }, [busy]);

  return (
    <Button size="sm" loading={busy} onClick={() => setBusy(true)}>
      Отправить
    </Button>
  );
};

// Где продакту искать каждый лоудер: экран, что именно мигает, какой это паттерн.
const loaderPlaces = [
  {
    screen: 'Discover – лента заявок',
    to: '/{-$locale}/dashboard/discover',
    what: 'Первая загрузка – карточки-заглушки в форме заявок',
    kind: 'skeleton',
  },
  {
    screen: 'Discover – лента заявок',
    to: '/{-$locale}/dashboard/discover',
    what: 'Догрузка следующей страницы при скролле – спиннер под списком',
    kind: 'spinner',
  },
  {
    screen: 'Discover – полоса сессий',
    to: '/{-$locale}/dashboard/discover',
    what: 'Ближайшие сессии сверху – до ответа сервера',
    kind: 'skeleton',
  },
  {
    screen: 'Discover – календарь',
    to: '/{-$locale}/dashboard/discover',
    what: 'Боковой календарь сессий при загрузке',
    kind: 'skeleton',
  },
  {
    screen: 'Чаты – список диалогов',
    to: '/{-$locale}/dashboard/chats',
    what: 'Строки диалогов, пока грузится список',
    kind: 'skeleton',
  },
  {
    screen: 'Чаты – переписка',
    to: '/{-$locale}/dashboard/chats',
    what: 'История сообщений при открытии диалога',
    kind: 'skeleton',
  },
  {
    screen: 'Мой профиль',
    to: '/{-$locale}/dashboard/profile',
    what: 'Два блока-заглушки вместо карточек',
    kind: 'skeleton',
  },
  {
    screen: 'Профиль игрока – игры',
    to: '/{-$locale}/dashboard/profile',
    what: 'Плитки игр, пока тянутся данные провайдеров',
    kind: 'skeleton',
  },
  {
    screen: 'Карточка заявки',
    to: '/{-$locale}/dashboard/discover',
    what: 'Ссылка-приглашение, пока сервер не выдал код',
    kind: 'skeleton',
  },
  {
    screen: 'Создание заявки',
    to: '/{-$locale}/dashboard/discover/create',
    what: 'Картинка-превью и кнопка публикации',
    kind: 'button',
  },
  {
    screen: 'Вход и регистрация',
    to: '/{-$locale}/sign-in',
    what: 'После клика по Discord/Google – форма прячется под крутилку',
    kind: 'warn',
  },
] as const;

const KIND_LABEL = {
  skeleton: { text: 'Skeleton', color: 'fg.success' },
  spinner: { text: 'Спиннер 500 мс', color: 'fg.info' },
  button: { text: 'Button loading', color: 'fg.info' },
  warn: { text: 'Спиннер сразу', color: 'fg.error' },
} as const;

// ── Page ─────────────────────────────────────────────────────────────

const DesignSystemPage = () => {
  return (
    <Box maxW="1000px" mx="auto" px={4} py={8}>
      <Stack gap={12}>
        {/* Header */}
        <Stack gap={2}>
          <Text textStyle="3xl" fontWeight={800}>
            Teamplay Design System
          </Text>
          <Text textStyle="body" color="fg.secondary">
            Semantic tokens, text styles, colors, and component patterns. Usage counts show how many
            times each token is used across the codebase.
          </Text>
        </Stack>

        {/* ── Typography ─────────────────────────────────────────── */}
        <SectionTitle title="Text Styles">
          <Stack gap={2}>
            {textStyles.map((ts) => (
              <Flex
                key={ts.name}
                alignItems="center"
                gap={4}
                bg="bg.surface"
                px={5}
                py={3}
                borderRadius="xl"
              >
                <HStack w="160px" flexShrink={0} gap={2}>
                  <Text textStyle="title" color="purple.200" fontFamily="mono">
                    {ts.name}
                  </Text>
                  <UseBadge count={ts.uses} />
                </HStack>
                <Text textStyle="caption" color="fg.muted" w="80px" flexShrink={0}>
                  {ts.desc}
                </Text>
                <Text textStyle={ts.name} color="fg">
                  {ts.sample}
                </Text>
              </Flex>
            ))}
          </Stack>
        </SectionTitle>

        {/* ── Foreground Colors ──────────────────────────────────── */}
        <SectionTitle title="Foreground Colors (fg.*)">
          <Stack gap={2}>
            {fgTokens.map((t) => (
              <HStack key={t.name} gap={4} bg="bg.surface" px={5} py={2.5} borderRadius="xl">
                <HStack w="160px" flexShrink={0} gap={2}>
                  <Text textStyle="title" color="purple.200" fontFamily="mono">
                    {t.name}
                  </Text>
                  <UseBadge count={t.uses} />
                </HStack>
                <Text textStyle="caption" color="fg.muted" w="120px" flexShrink={0}>
                  {t.value}
                </Text>
                <Text textStyle="body" color={t.name} flex={1}>
                  {t.desc}
                </Text>
              </HStack>
            ))}
          </Stack>
        </SectionTitle>

        {/* ── Background Colors ──────────────────────────────────── */}
        <SectionTitle title="Background Colors (bg.*)">
          <Grid templateColumns="repeat(auto-fill, minmax(220px, 1fr))" gap={3}>
            {bgTokens.map((t) => (
              <Stack key={t.name} bg="bg.surface" borderRadius="xl" overflow="hidden">
                <Box
                  h="56px"
                  bg={t.token}
                  borderWidth="1px"
                  borderColor="border.subtle"
                  borderTopRadius="xl"
                />
                <HStack px={3} py={2} justifyContent="space-between">
                  <Stack gap={0}>
                    <Text textStyle="title" color="purple.200" fontFamily="mono">
                      {t.name}
                    </Text>
                    <Text textStyle="caption" color="fg.muted">
                      {t.desc}
                    </Text>
                    <Text textStyle="micro" color="fg.subtle">
                      {t.value}
                    </Text>
                  </Stack>
                  <UseBadge count={t.uses} />
                </HStack>
              </Stack>
            ))}
          </Grid>
        </SectionTitle>

        {/* ── Border Tokens ──────────────────────────────────────── */}
        <SectionTitle title="Border Tokens">
          <Grid templateColumns="repeat(auto-fill, minmax(220px, 1fr))" gap={3}>
            {borderTokens.map((t) => (
              <HStack
                key={t.name}
                bg="bg.surface"
                borderRadius="xl"
                px={4}
                py={3}
                borderWidth="2px"
                borderColor={t.name}
                justifyContent="space-between"
              >
                <Stack gap={0}>
                  <Text textStyle="title" color="purple.200" fontFamily="mono">
                    {t.name}
                  </Text>
                  <Text textStyle="caption" color="fg.muted">
                    {t.desc}
                  </Text>
                  <Text textStyle="micro" color="fg.subtle">
                    {t.value}
                  </Text>
                </Stack>
                <UseBadge count={t.uses} />
              </HStack>
            ))}
          </Grid>
        </SectionTitle>

        {/* ── Interactive States ──────────────────────────────────── */}
        <SectionTitle title="Interactive States">
          <HStack gap={3} flexWrap="wrap">
            {[
              { label: 'Default', bg: 'transparent', border: 'border.subtle' },
              { label: 'Hover / Active', bg: 'bg.muted', border: 'border.subtle' },
              { label: 'Selected', bg: 'bg.muted', border: 'border' },
            ].map((s) => (
              <Box
                key={s.label}
                px={5}
                py={2.5}
                borderRadius="lg"
                bg={s.bg}
                borderWidth="1px"
                borderColor={s.border}
              >
                <Text textStyle="title">{s.label}</Text>
                <Text textStyle="caption" color="fg.muted">
                  {s.bg}
                </Text>
              </Box>
            ))}
          </HStack>
        </SectionTitle>

        {/* ── Status Badges ──────────────────────────────────────── */}
        <SectionTitle title="Status Badges">
          <Text textStyle="body" color="fg.secondary" mt={-2}>
            Здесь стоят настоящие компоненты, а не их копии: ровно то, что рисуется в ленте, в
            календаре и на странице заявки. Правка компонента меняет и эту страницу – иначе
            витрина показывает стиль, которого в продукте нет.
          </Text>
          <HStack gap={3} flexWrap="wrap">
            <SessionLiveBadge />
            <SessionEndedBadge>Завершена</SessionEndedBadge>
            <Chip variant="status" colorPalette="blue">
              Upcoming
            </Chip>
            <Chip variant="status" colorPalette="orange">
              Expiring
            </Chip>
            <Chip variant="status" colorPalette="red">
              Error
            </Chip>
            <Chip variant="status" colorPalette="teal">
              Link text
            </Chip>
          </HStack>
        </SectionTitle>

        {/* ── Живые компоненты ───────────────────────────────────── */}
        {/*
          Раздел существует по одной причине: страница дизайн-системы, которая перерисовывает
          компоненты своей вёрсткой, врёт. Её бейдж «идёт сейчас» не совпадал ни с одним в
          продукте, а сверялись по нему. Здесь стоят импорты, и только они: если компонента нет
          в этом списке — значит его нет и в продукте.
        */}
        <SectionTitle title="Живые компоненты">
          <Text textStyle="body" color="fg.secondary" mt={-2}>
            Импорт, а не вёрстка. Правка компонента меняет и продукт, и эту страницу одновременно –
            расхождение становится видно в тот же день, а не через полгода.
          </Text>

          <Stack gap={5}>
            <Stack gap={3} bg="bg.surface" borderRadius="xl" p={5}>
              <Text textStyle="caption" color="fg.muted" fontFamily="mono">
                SectionHeading · подпись раздела
              </Text>
              <SectionHeading count={5} hint="за последние сутки">
                Ближайшие сессии
              </SectionHeading>
              <Text textStyle="caption" color="fg.subtle">
                Один приём на все разделы: 16/600 и серая пилюля-счётчик. Капслок с разрядкой
                остаётся только внутри игровых стат-плашек – там это метка величины, а не заголовок.
              </Text>
            </Stack>

            <Stack gap={3} bg="bg.surface" borderRadius="xl" p={5}>
              <Text textStyle="caption" color="fg.muted" fontFamily="mono">
                Panel · три роли поверхности
              </Text>
              <HStack gap={3} flexWrap="wrap" alignItems="stretch">
                <Panel variant="object" px={4} py={3} minW="150px">
                  <Text textStyle="title">object</Text>
                  <Text textStyle="caption" color="fg.muted">
                    карточка, плитка, день
                  </Text>
                </Panel>
                <Panel variant="container" px={4} py={3} minW="150px">
                  <Text textStyle="title">container</Text>
                  <Text textStyle="caption" color="fg.muted">
                    колонка, список
                  </Text>
                </Panel>
                <Panel variant="nested" px={4} py={3} minW="150px">
                  <Text textStyle="title">nested</Text>
                  <Text textStyle="caption" color="fg.muted">
                    блок внутри
                  </Text>
                </Panel>
              </HStack>
              <Text textStyle="caption" color="fg.subtle">
                Роль отвечает на «чем эта коробка является», а не «как она выглядит». Предмет имеет
                край – поэтому граница; контейнер не предмет, у него глубина – поэтому тень;
                вложенное не должно читаться коробкой того же уровня – поэтому только заливка.
              </Text>
            </Stack>

            <Stack gap={3} bg="bg.surface" borderRadius="xl" p={5}>
              <Text textStyle="caption" color="fg.muted" fontFamily="mono">
                Chip · три варианта рецепта
              </Text>
              <HStack gap={3} flexWrap="wrap">
                <Chip variant="fact">PC, PlayStation</Chip>
                <Chip variant="status" colorPalette="green">
                  Мест хватает
                </Chip>
                <Chip variant="media">18+</Chip>
              </HStack>
              <Text textStyle="caption" color="fg.subtle">
                Факт о человеке или заявке · состояние семантическим цветом · число поверх обложки.
                Четвёртого варианта нет: пилюля, слепленная руками, – это всегда один из этих трёх.
              </Text>
            </Stack>

            <Stack gap={3} bg="bg.surface" borderRadius="xl" p={5}>
              <Text textStyle="caption" color="fg.muted" fontFamily="mono">
                SessionMemberAvatars + SessionSeatsCount · состав и места
              </Text>
              <HStack gap={6} flexWrap="wrap">
                {[
                  { seats: { filled: 3, total: 5, open: 2 }, note: 'места есть' },
                  { seats: { filled: 4, total: 5, open: 1 }, note: 'осталось одно' },
                  { seats: { filled: 5, total: 5, open: 0 }, note: 'состав полный' },
                ].map((item) => (
                  <HStack key={item.note} gap={2}>
                    <SessionMemberAvatars
                      size="2xs"
                      avatars={[
                        { key: 'a', avatar: null, name: 'Артём' },
                        { key: 'b', avatar: null, name: 'Максим' },
                        { key: 'c', avatar: null, name: 'Кирилл' },
                      ]}
                    />
                    <SessionSeatsCount seats={item.seats} />
                    <Text textStyle="caption" color="fg.subtle">
                      {item.note}
                    </Text>
                  </HStack>
                ))}
              </HStack>
              <Text textStyle="caption" color="fg.subtle">
                Цвет дроби отвечает на «могу ли я сесть», а не украшает: зелёный – места есть,
                оранжевый – последнее, серый – полно.
              </Text>
            </Stack>

            <Stack gap={3} bg="bg.surface" borderRadius="xl" p={5}>
              <Text textStyle="caption" color="fg.muted" fontFamily="mono">
                ListEmpty · пустой список внутри экрана
              </Text>
              <Box bg="bg" borderRadius="lg">
                <ListEmpty
                  icon={CalendarTimeIcon}
                  title="Сессий пока нет"
                  description="Соберите первую — команда подтянется"
                />
              </Box>
              <Text textStyle="caption" color="fg.subtle">
                Роль — пустой список там, где рядом есть содержимое: вкладка друзей над блоком
                рекомендаций, колонка чатов рядом с открытой перепиской. Кнопки необязательны и по
                умолчанию не нужны — действие уже на экране.
              </Text>
            </Stack>

            <Stack gap={3} bg="bg.surface" borderRadius="xl" p={5}>
              <Text textStyle="caption" color="fg.muted" fontFamily="mono">
                IconButton · кнопка без подписи · 30 файлов
              </Text>
              <HStack gap={3} flexWrap="wrap">
                <IconButton aria-label="Ответить">
                  <MicOnIcon />
                </IconButton>
                <IconButton aria-label="Позвать" colorPalette="purple">
                  <AddIcon />
                </IconButton>
                <IconButton aria-label="Выключить" disabled>
                  <MicOffIcon />
                </IconButton>
                <CloseButton />
              </HStack>
              <Text textStyle="caption" color="fg.subtle">
                Самый переиспользуемый компонент библиотеки после уведомлений. Имя кнопки задаётся
                внутри, а не на каждом вызове: их десятки, и без этого они звучат по-разному для
                тех, кто слушает страницу голосом.
              </Text>
            </Stack>

            <Stack gap={3} bg="bg.surface" borderRadius="xl" p={5}>
              <Text textStyle="caption" color="fg.muted" fontFamily="mono">
                PersonRow · строка человека · 5 файлов
              </Text>
              <Stack gap={0} bg="bg" borderRadius="lg" py={1}>
                <PersonRow userId="showcase-1" displayName="Артём" badge="Друг" />
                <PersonRow userId="showcase-2" displayName="Максим" />
              </Stack>
              <Text textStyle="caption" color="fg.subtle">
                Одна строка на все списки людей: связи, рекомендации, состав сессии. Раньше рядом
                жила её копия на 275 строк, ровно потому, что найти оригинал было негде.
              </Text>
            </Stack>

            <Stack gap={3} bg="bg.surface" borderRadius="xl" p={5}>
              <Text textStyle="caption" color="fg.muted" fontFamily="mono">
                GamePresence · во что играет · 5 файлов
              </Text>
              <HStack gap={4} flexWrap="wrap">
                <GamePresence
                  variant="chip"
                  presence={{ state: 'MATCH', gameName: 'Dota 2', source: 'STEAM' }}
                />
                <GamePresence
                  variant="chip"
                  presence={{ state: 'IN_GAME', gameName: 'CS2', source: 'STEAM' }}
                />
                <GamePresence
                  variant="line"
                  presence={{ state: 'RECENT', gameName: 'Apex Legends', playedAgo: '3 ч назад' }}
                />
              </HStack>
              <Text textStyle="caption" color="fg.subtle">
                Красится только совпадение с твоей игрой: это состояние, по которому решают, звать
                ли человека. «Играет во что-то другое» и «играл недавно» это обычные факты.
              </Text>
            </Stack>

            <Stack gap={3} bg="bg.surface" borderRadius="xl" p={5}>
              <Text textStyle="caption" color="fg.muted" fontFamily="mono">
                Menu · меню действий · 12 файлов
              </Text>
              <MenuRoot>
                <MenuTrigger asChild>
                  <Button size="sm" variant="outline">
                    Действия
                  </Button>
                </MenuTrigger>
                <MenuContent>
                  <MenuItem value="edit">Изменить</MenuItem>
                  <MenuItem value="share">Поделиться</MenuItem>
                  <MenuItem value="delete" color="fg.error">
                    Удалить
                  </MenuItem>
                </MenuContent>
              </MenuRoot>
              <Text textStyle="caption" color="fg.subtle">
                Опасное действие красится, остальные нет: цвет здесь отвечает на «что будет, если
                нажать», а не украшает список.
              </Text>
            </Stack>

            <Stack gap={3} bg="bg.surface" borderRadius="xl" p={5}>
              <Text textStyle="caption" color="fg.muted" fontFamily="mono">
                Field · подпись, подсказка, ошибка · 10 файлов
              </Text>
              <Stack gap={4} maxW="380px">
                <Field label="Ник" helperText="Так тебя увидят в заявках">
                  <Input placeholder="Артём" />
                </Field>
                <Field label="Почта" errorText="Такая почта уже занята" invalid>
                  <Input placeholder="mail@example.com" />
                </Field>
              </Stack>
              <Text textStyle="caption" color="fg.subtle">
                Подсказка и ошибка занимают одно место: ошибка заменяет подсказку, а не встаёт
                под ней, иначе форма прыгает на каждой опечатке.
              </Text>
            </Stack>

            <Stack gap={3} bg="bg.surface" borderRadius="xl" p={5}>
              <Text textStyle="caption" color="fg.muted" fontFamily="mono">
                RadioCard · выбор одного · CheckboxCard · выбор нескольких
              </Text>
              <HStack gap={4} flexWrap="wrap" alignItems="stretch">
                <RadioCardRoot defaultValue="pc" maxW="320px">
                  <HStack gap={2}>
                    <RadioCardItem value="pc" label="PC" />
                    <RadioCardItem value="ps" label="PlayStation" />
                  </HStack>
                </RadioCardRoot>
                <CheckboxCard label="Микрофон" description="Готов говорить" minW="180px" />
              </HStack>
              <Text textStyle="caption" color="fg.subtle">
                Одинаковые снаружи, разные по смыслу: круг это выбор одного, квадрат это выбор
                нескольких. Форма индикатора и есть ответ на «сколько можно отметить».
              </Text>
            </Stack>

            <Stack gap={3} bg="bg.surface" borderRadius="xl" p={5}>
              <Text textStyle="caption" color="fg.muted" fontFamily="mono">
                Clipboard · скопировать · OnlinePresence · точка присутствия
              </Text>
              <HStack gap={6} flexWrap="wrap" alignItems="center">
                <ClipboardRoot value="https://teamplay.gg/invite/demo" maxW="280px">
                  <ClipboardInput />
                </ClipboardRoot>
                <HStack gap={4}>
                  <OnlinePresence status="ONLINE" userId="showcase-online" />
                  <OnlinePresence status="AWAY" userId="showcase-away" />
                  <OnlinePresence status="OFFLINE" userId="showcase-offline" />
                </HStack>
              </HStack>
              <Text textStyle="caption" color="fg.subtle">
                Точка отделяется от аватара вырезом, а не крашеным кольцом под цвет фона: кольцо
                приходилось подгонять руками в десяти местах, и на двух поверхностях верного
                цвета не существовало вовсе.
              </Text>
            </Stack>

            <Stack gap={3} bg="bg.surface" borderRadius="xl" p={5}>
              <Text textStyle="caption" color="fg.muted" fontFamily="mono">
                Placeholder · пустая страница
              </Text>
              {/*
                Верхний отступ компонента рассчитан на пустую страницу целиком (160px на широком
                экране). В витрине он съел бы полэкрана, поэтому демонстрация обрезана рамкой —
                сам компонент при этом настоящий, без подмены отступов.
              */}
              <Box bg="bg" borderRadius="lg" maxH="200px" overflow="hidden" display="flex">
                <Placeholder icon={<Text textStyle="3xl">◎</Text>}>
                  <Placeholder.Title>Сессий пока нет</Placeholder.Title>
                  <Placeholder.Description>
                    Соберите первую – команда подтянется
                  </Placeholder.Description>
                </Placeholder>
              </Box>
              <Text textStyle="caption" color="fg.subtle">
                Иконка, заголовок, описание и необязательная кнопка. Экран, которому нужны кнопки,
                добавляет их сюда, а не заводит своё пустое состояние. Верхний отступ компонента
                рассчитан на пустую страницу – здесь он обрезан рамкой.
              </Text>
            </Stack>
          </Stack>
        </SectionTitle>

        {/* ── UI Elements ────────────────────────────────────────── */}
        <SectionTitle title="UI Elements">
          <Stack gap={6}>
            {/* Buttons */}
            <Stack gap={2}>
              <Text textStyle="title" color="fg.secondary">
                Buttons
              </Text>
              <HStack gap={3} flexWrap="wrap">
                <Button size="sm">Primary</Button>
                <Button size="sm" variant="outline">
                  Outline
                </Button>
                <Button size="sm" variant="ghost">
                  Ghost
                </Button>
                <Button size="sm" variant="subtle">
                  Subtle
                </Button>
                <Button size="sm" colorPalette="red">
                  Danger
                </Button>
                <Button size="sm" disabled>
                  Disabled
                </Button>
              </HStack>
            </Stack>

            {/* Tags */}
            <Stack gap={2}>
              <Text textStyle="title" color="fg.secondary">
                Tags
              </Text>
              <HStack gap={2} flexWrap="wrap">
                <Tag.Root size="lg" colorPalette="purple">
                  <Tag.Label>Competitive</Tag.Label>
                </Tag.Root>
                <Tag.Root size="lg" colorPalette="green">
                  <Tag.Label>Casual</Tag.Label>
                </Tag.Root>
                <Tag.Root size="lg" colorPalette="blue">
                  <Tag.Label>Ranked</Tag.Label>
                </Tag.Root>
                <Tag.Root size="lg" colorPalette="yellow">
                  <Tag.Label>Beginner</Tag.Label>
                </Tag.Root>
              </HStack>
            </Stack>

            {/* Cards */}
            <Stack gap={2}>
              <Text textStyle="title" color="fg.secondary">
                Card Surfaces
              </Text>
              <HStack gap={3} flexWrap="wrap">
                {[
                  { bg: 'bg.surface', label: 'bg.surface' },
                  { bg: 'bg.card', label: 'bg.card' },
                  { bg: 'bg.emphasized', label: 'bg.emphasized' },
                ].map((c) => (
                  <Box
                    key={c.label}
                    bg={c.bg}
                    borderRadius="xl"
                    px={5}
                    py={4}
                    w="200px"
                    borderWidth="1px"
                    borderColor="border.subtle"
                  >
                    <Text textStyle="title" mb={1}>
                      Card Title
                    </Text>
                    <Text textStyle="body" color="fg.muted">
                      Description text on {c.label}
                    </Text>
                    <Text textStyle="caption" color="fg.subtle" mt={2}>
                      {c.label}
                    </Text>
                  </Box>
                ))}
              </HStack>
            </Stack>

            {/* Text Hierarchy Example */}
            <Stack gap={2}>
              <Text textStyle="title" color="fg.secondary">
                Text Hierarchy
              </Text>
              <Box bg="bg.card" borderRadius="xl" px={5} py={4} maxW="400px">
                <Text textStyle="heading" mb={1}>
                  ALEXdddddddddd
                </Text>
                <HStack gap={2} mb={3}>
                  <Text textStyle="caption" color="fg.muted">
                    13 years
                  </Text>
                  <Text textStyle="caption" color="fg.subtle">
                    ·
                  </Text>
                  <Text textStyle="caption" color="fg.muted">
                    Male
                  </Text>
                  <Text textStyle="caption" color="fg.subtle">
                    ·
                  </Text>
                  <Text textStyle="caption" color="fg.muted">
                    Mic on
                  </Text>
                </HStack>
                <Text textStyle="body" color="fg.secondary" mb={3}>
                  Looking for teammates to play Apex Legends ranked. Diamond+ preferred.
                </Text>
                <HStack gap={2}>
                  <SessionLiveBadge />
                  <Chip variant="fact" size="sm" fontVariantNumeric="tabular-nums">
                    57:04
                  </Chip>
                  <Box ml="auto">
                    <SessionSeatsCount seats={{ filled: 1, total: 2, open: 1 }} />
                  </Box>
                </HStack>
              </Box>
            </Stack>
          </Stack>
        </SectionTitle>

        {/* ── Semantic Color Roles ─────────────────────────────── */}
        <SectionTitle title="Semantic Color Roles">
          <Text textStyle="body" color="fg.secondary" mt={-2}>
            Each role maps tokens to UI elements. Change the token – all elements update.
          </Text>

          {/* ── Error Role ── */}
          <Stack gap={4} bg="bg.surface" borderRadius="xl" p={5}>
            <HStack gap={3}>
              <Circle size="10px" bg="fg.error" />
              <Text textStyle="subtitle">Error</Text>
              <Text textStyle="caption" color="fg.muted">
                Destructive / irreversible actions
              </Text>
            </HStack>

            {/* Tokens */}
            <Grid templateColumns="repeat(auto-fill, minmax(180px, 1fr))" gap={2}>
              {[
                { token: 'fg.error', value: 'red.400' },
                { token: 'red.solid', value: 'red.500' },
                { token: 'bg.error', value: 'red.950' },
                { token: 'border.error', value: 'red.400' },
              ].map((t) => (
                <HStack key={t.token} gap={2} px={3} py={2} bg="bg.muted" borderRadius="lg">
                  <Circle
                    size="24px"
                    bg={
                      t.token.startsWith('bg.') || t.token.endsWith('.solid') ? t.token : undefined
                    }
                    borderWidth={t.token.startsWith('border.') ? '2px' : undefined}
                    borderColor={t.token.startsWith('border.') ? t.token : undefined}
                  >
                    {t.token.startsWith('fg.') && (
                      <Text textStyle="strong" color={t.token}>
                        A
                      </Text>
                    )}
                  </Circle>
                  <Stack gap={0}>
                    <Text textStyle="micro" fontFamily="mono" color="purple.200">
                      {t.token}
                    </Text>
                    <Text textStyle="micro" color="fg.subtle">
                      {t.value}
                    </Text>
                  </Stack>
                </HStack>
              ))}
            </Grid>

            {/* Buttons */}
            <Stack gap={1}>
              <Text textStyle="caption" color="fg.muted">
                Buttons – colorPalette=&quot;red&quot;
              </Text>
              <HStack gap={3} flexWrap="wrap">
                <Button size="sm" colorPalette="red">
                  Delete
                </Button>
                <Button size="sm" colorPalette="red" variant="outline">
                  Cancel
                </Button>
                <Button size="sm" colorPalette="red" variant="ghost">
                  Leave
                </Button>
                <Button size="sm" colorPalette="red" variant="subtle">
                  Decline
                </Button>
              </HStack>
            </Stack>

            {/* Badge + Toast */}
            <Stack gap={1}>
              <Text textStyle="caption" color="fg.muted">
                Badges &amp; Toasts
              </Text>
              <HStack gap={3} flexWrap="wrap" alignItems="center">
                <Flex
                  h={7}
                  px={3}
                  borderRadius="full"
                  bg="red.500/10"
                  color="fg.error"
                  textStyle="badge"
                  alignItems="center"
                >
                  CANCELLED
                </Flex>
                <Box bg="red.solid" color="red.contrast" px={3} py={2} borderRadius="l2">
                  <Text textStyle="micro" fontWeight="medium">
                    Error toast
                  </Text>
                </Box>
              </HStack>
            </Stack>

            {/* Menu + Text */}
            <Stack gap={1}>
              <Text textStyle="caption" color="fg.muted">
                Menu Items &amp; Text
              </Text>
              <HStack gap={4} flexWrap="wrap" alignItems="flex-start">
                <Box bg="bg.muted" borderRadius="lg" px={1} py={1} minW="180px">
                  <Box
                    px={3}
                    py={1.5}
                    borderRadius="md"
                    _hover={{ bg: 'whiteAlpha.300' }}
                    cursor="pointer"
                  >
                    <Text textStyle="body" color="fg.error">
                      Delete post
                    </Text>
                  </Box>
                  <Box
                    px={3}
                    py={1.5}
                    borderRadius="md"
                    _hover={{ bg: 'whiteAlpha.300' }}
                    cursor="pointer"
                  >
                    <Text textStyle="body" color="fg.error">
                      Delete comment
                    </Text>
                  </Box>
                </Box>
                <Stack gap={1}>
                  <Text textStyle="sm" color="fg.error">
                    Form validation error
                  </Text>
                  <Text textStyle="sm" color="fg.error">
                    Link preview error
                  </Text>
                </Stack>
              </HStack>
            </Stack>

            <Text textStyle="caption" color="fg.subtle">
              Delete chat/post/comment/account/session, Remove member, Cancel session, Decline
              invite, Leave session, Error toasts, CANCELLED badge, Form validation, Link preview
              errors
            </Text>
          </Stack>

          {/* ── Warning Role ── */}
          <Stack gap={4} bg="bg.surface" borderRadius="xl" p={5}>
            <HStack gap={3}>
              <Circle size="10px" bg="fg.warning" />
              <Text textStyle="subtitle">Warning</Text>
              <Text textStyle="caption" color="fg.muted">
                Caution / reversible / attention
              </Text>
            </HStack>

            {/* Tokens */}
            <Grid templateColumns="repeat(auto-fill, minmax(180px, 1fr))" gap={2}>
              {[
                { token: 'fg.warning', value: 'orange.200' },
                { token: 'orange.solid', value: 'orange.400' },
                { token: 'bg.warning', value: 'orange.950' },
                { token: 'border.warning', value: 'orange.400' },
              ].map((t) => (
                <HStack key={t.token} gap={2} px={3} py={2} bg="bg.muted" borderRadius="lg">
                  <Circle
                    size="24px"
                    bg={
                      t.token.startsWith('bg.') || t.token.endsWith('.solid') ? t.token : undefined
                    }
                    borderWidth={t.token.startsWith('border.') ? '2px' : undefined}
                    borderColor={t.token.startsWith('border.') ? t.token : undefined}
                  >
                    {t.token.startsWith('fg.') && (
                      <Text textStyle="strong" color={t.token}>
                        A
                      </Text>
                    )}
                  </Circle>
                  <Stack gap={0}>
                    <Text textStyle="micro" fontFamily="mono" color="purple.200">
                      {t.token}
                    </Text>
                    <Text textStyle="micro" color="fg.subtle">
                      {t.value}
                    </Text>
                  </Stack>
                </HStack>
              ))}
            </Grid>

            {/* Buttons */}
            <Stack gap={1}>
              <Text textStyle="caption" color="fg.muted">
                Buttons – colorPalette=&quot;orange&quot; &amp; fg.warning
              </Text>
              <HStack gap={3} flexWrap="wrap">
                <Button size="sm" colorPalette="orange" variant="outline">
                  Complete session
                </Button>
                <Button size="sm" colorPalette="orange">
                  Yes
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  color="fg.warning"
                  borderColor="fg.warning"
                  _hover={{ color: 'fg.error', borderColor: 'fg.error' }}
                >
                  Revoke
                </Button>
                <Button size="sm" variant="ghost" color="fg.warning" _hover={{ color: 'fg.error' }}>
                  Revoke (ghost)
                </Button>
              </HStack>
            </Stack>

            {/* Badges + Toast */}
            <Stack gap={1}>
              <Text textStyle="caption" color="fg.muted">
                Badges &amp; Toasts
              </Text>
              <HStack gap={3} flexWrap="wrap" alignItems="center">
                <Flex bgColor="orange.500/15" px={3} py={1.5} borderRadius="lg">
                  <Text textStyle="title" color="fg.warning">
                    Invited
                  </Text>
                </Flex>
                <Flex
                  h={7}
                  px={3}
                  borderRadius="full"
                  bg="orange.500/10"
                  color="fg.warning"
                  textStyle="badge"
                  alignItems="center"
                >
                  EXPIRING
                </Flex>
                <Box bg="orange.solid" color="orange.contrast" px={3} py={2} borderRadius="l2">
                  <Text textStyle="micro" fontWeight="medium">
                    Warning toast
                  </Text>
                </Box>
              </HStack>
            </Stack>

            {/* Banner */}
            <Stack gap={1}>
              <Text textStyle="caption" color="fg.muted">
                System Banner – bg.warning / border.warning / fg.warning
              </Text>
              <Box
                bg="bg.warning"
                borderWidth="1px"
                borderColor="border.warning"
                borderRadius="lg"
                px={4}
                py={2.5}
                maxW="400px"
              >
                <Text textStyle="body" color="fg.warning">
                  Sessions are temporarily unavailable
                </Text>
              </Box>
            </Stack>

            {/* Menu + Text + Dot */}
            <Stack gap={1}>
              <Text textStyle="caption" color="fg.muted">
                Menu Items, Warning Text &amp; Notification Dot
              </Text>
              <HStack gap={4} flexWrap="wrap" alignItems="flex-start">
                <Box bg="bg.muted" borderRadius="lg" px={1} py={1} minW="180px">
                  <Box
                    px={3}
                    py={1.5}
                    borderRadius="md"
                    _hover={{ bg: 'whiteAlpha.300' }}
                    cursor="pointer"
                  >
                    <Text textStyle="body" color="fg.warning">
                      Block user
                    </Text>
                  </Box>
                </Box>
                <Stack gap={1}>
                  <Text textStyle="sm" color="fg.warning">
                    Character limit warning
                  </Text>
                  <Text textStyle="sm" color="fg.warning">
                    Temporarily unavailable
                  </Text>
                  <Text textStyle="sm" color="fg.warning">
                    Session starting in 2h
                  </Text>
                  <Text textStyle="sm" color="fg.warning">
                    Post form warning
                  </Text>
                </Stack>
                <Stack gap={1} alignItems="center">
                  <Box position="relative" w="32px" h="32px" bg="bg.muted" borderRadius="md">
                    <Circle size="8px" bg="yellow.200" position="absolute" top="2px" right="2px" />
                  </Box>
                  <Text textStyle="micro" color="fg.subtle">
                    yellow.200
                  </Text>
                </Stack>
              </HStack>
            </Stack>

            <Text textStyle="caption" color="fg.subtle">
              Complete session, Revoke invite, Invited badge, Block user, Char limit, Session timer,
              Profile warnings, Post form warning, System banners, Warning toasts (member left,
              session completed), EXPIRING badge, Notification unread dot (yellow.200)
            </Text>
          </Stack>
          {/* ── Purple ── */}
          <Stack gap={4} bg="bg.surface" borderRadius="xl" p={5}>
            <HStack gap={3}>
              <Circle size="10px" bg="purple.200" />
              <Text textStyle="subtitle">Purple – Primary Accent</Text>
            </HStack>

            <Stack gap={1}>
              <Text textStyle="caption" color="fg.muted">
                Buttons – colorPalette=&quot;purple&quot;
              </Text>
              <HStack gap={3} flexWrap="wrap">
                <Button size="sm">Primary (default)</Button>
                <Button size="sm" variant="outline" colorPalette="purple">
                  Outline
                </Button>
                <Button size="sm" variant="ghost" colorPalette="purple">
                  Ghost
                </Button>
                <Button size="sm" variant="subtle" colorPalette="purple">
                  Subtle
                </Button>
              </HStack>
            </Stack>

            <Stack gap={1}>
              <Text textStyle="caption" color="fg.muted">
                Text &amp; Icons – purple.200 / purple.300
              </Text>
              <HStack gap={4} flexWrap="wrap">
                <Text textStyle="title" color="purple.200">
                  Author name
                </Text>
                <Text textStyle="title" color="purple.300">
                  Comment author
                </Text>
                <Text textStyle="body" color="purple.200">
                  Chat counter
                </Text>
              </HStack>
            </Stack>

            <Text textStyle="caption" color="fg.subtle">
              Primary buttons, Switches, Author names, Comment highlights, Chat counters, Discord
              management, Onboarding, Avatar crop, Link account, Pagination
            </Text>
          </Stack>

          {/* ── Green ── */}
          <Stack gap={4} bg="bg.surface" borderRadius="xl" p={5}>
            <HStack gap={3}>
              <Circle size="10px" bg="fg.success" />
              <Text textStyle="subtitle">Green – Success</Text>
            </HStack>

            <HStack gap={3} flexWrap="wrap" alignItems="center">
              <SessionLiveBadge />
              <SessionEndedBadge>COMPLETED</SessionEndedBadge>
              <Chip variant="status" colorPalette="green" size="sm">
                Accepted
              </Chip>
              <Box bg="green.solid" color="green.contrast" px={3} py={2} borderRadius="l2">
                <Text textStyle="micro" fontWeight="medium">
                  Success toast
                </Text>
              </Box>
            </HStack>

            <Text textStyle="caption" color="fg.subtle">
              LIVE badge, COMPLETED badge, Accepted member, Success toasts, Online indicator, Game
              activity stats
            </Text>
          </Stack>

          {/* ── Teal / Links ── */}
          <Stack gap={4} bg="bg.surface" borderRadius="xl" p={5}>
            <HStack gap={3}>
              <Circle size="10px" bg="fg.link" />
              <Text textStyle="subtitle">Teal – Links &amp; Interactive</Text>
            </HStack>

            <HStack gap={4} flexWrap="wrap">
              <Text textStyle="body" color="fg.link">
                fg.link – Link text
              </Text>
              <Text textStyle="body" color="fg.link.hover">
                fg.link.hover – Hovered / edit
              </Text>
              <Text textStyle="body" color="teal.400">
                teal.400 – Time display, form hints
              </Text>
            </HStack>

            <Text textStyle="caption" color="fg.subtle">
              Navigation links, Sign-in/up links, Edit buttons, User time display, Session form
              hints, Settings links
            </Text>
          </Stack>

          {/* ── Blue ── */}
          <Stack gap={4} bg="bg.surface" borderRadius="xl" p={5}>
            <HStack gap={3}>
              <Circle size="10px" bg="fg.info" />
              <Text textStyle="subtitle">Blue – Info</Text>
            </HStack>

            <HStack gap={3} flexWrap="wrap" alignItems="center">
              <Flex
                h={7}
                px={3}
                borderRadius="full"
                bg="blue.500/10"
                color="fg.info"
                textStyle="badge"
                alignItems="center"
              >
                UPCOMING
              </Flex>
              <Box bg="bg.panel" color="fg" px={3} py={2} borderRadius="l2" boxShadow="xl">
                <Text textStyle="micro" fontWeight="medium">
                  Info toast (bg.panel)
                </Text>
              </Box>
            </HStack>

            <Text textStyle="caption" color="fg.subtle">
              UPCOMING badge, Info toasts (new message, new comment, session invite), Loading
              spinner
            </Text>
          </Stack>

          {/* ── Gray ── */}
          <Stack gap={4} bg="bg.surface" borderRadius="xl" p={5}>
            <HStack gap={3}>
              <Circle size="10px" bg="gray.500" />
              <Text textStyle="subtitle">Gray – Secondary</Text>
            </HStack>

            <HStack gap={3} flexWrap="wrap">
              <Button size="sm" colorPalette="gray" variant="outline">
                Sort
              </Button>
              <Button size="sm" colorPalette="gray" variant="outline">
                Filters
              </Button>
              <Button size="sm" colorPalette="gray" variant="ghost">
                Close
              </Button>
            </HStack>

            <Text textStyle="caption" color="fg.subtle">
              Sort/Filter controls, Close/dismiss buttons, Secondary actions
            </Text>
          </Stack>
        </SectionTitle>

        {/* ── Toasts & Notifications ────────────────────────────── */}
        <SectionTitle title="Toasts &amp; Notifications">
          <Text textStyle="body" color="fg.secondary" mt={-2}>
            4 toast types via toaster.create(). Placement: top-end. Clickable when meta.href is set.
          </Text>

          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
            {/* Success */}
            <Stack
              gap={3}
              bg="green.solid"
              color="green.contrast"
              px={5}
              py={4}
              borderRadius="xl"
              position="relative"
            >
              <Stack gap={0}>
                <Text textStyle="title" fontWeight="medium">
                  Success – green.solid
                </Text>
                <Text textStyle="caption" opacity={0.8}>
                  Confirms completed actions
                </Text>
              </Stack>
              <Stack gap={0.5}>
                <Text textStyle="micro">Joined session successfully</Text>
                <Text textStyle="micro">Post deleted</Text>
                <Text textStyle="micro">Copied to clipboard</Text>
                <Text textStyle="micro">Registration successful</Text>
                <Text textStyle="micro">Password reset</Text>
                <Text textStyle="micro">Post created / edited</Text>
                <Text textStyle="micro">Feedback sent</Text>
                <Text textStyle="micro">Report submitted</Text>
                <Text textStyle="micro">Chat blocked / deleted</Text>
                <Text textStyle="micro">Added to favorites</Text>
              </Stack>
            </Stack>

            {/* Error */}
            <Stack gap={3} bg="red.solid" color="red.contrast" px={5} py={4} borderRadius="xl">
              <Stack gap={0}>
                <Text textStyle="title" fontWeight="medium">
                  Error – red.solid
                </Text>
                <Text textStyle="caption" opacity={0.8}>
                  API failures &amp; validation errors
                </Text>
              </Stack>
              <Stack gap={0.5}>
                <Text textStyle="micro">Sign in / sign up error</Text>
                <Text textStyle="micro">Session join/leave/cancel error</Text>
                <Text textStyle="micro">Message send failed</Text>
                <Text textStyle="micro">Message too long</Text>
                <Text textStyle="micro">Delete account error</Text>
                <Text textStyle="micro">Post create / edit error</Text>
                <Text textStyle="micro">Discord operation error</Text>
                <Text textStyle="micro">OAuth error</Text>
                <Text textStyle="micro">Copy failed</Text>
              </Stack>
            </Stack>

            {/* Warning */}
            <Stack
              gap={3}
              bg="orange.solid"
              color="orange.contrast"
              px={5}
              py={4}
              borderRadius="xl"
            >
              <Stack gap={0}>
                <Text textStyle="title" fontWeight="medium">
                  Warning – orange.solid
                </Text>
                <Text textStyle="caption" opacity={0.8}>
                  Realtime session events (WS)
                </Text>
              </Stack>
              <Stack gap={0.5}>
                <Text textStyle="micro">Player left the session</Text>
                <Text textStyle="micro">Player declined the invite</Text>
                <Text textStyle="micro">Session completed</Text>
                <Text textStyle="micro">Session cancelled</Text>
              </Stack>
            </Stack>

            {/* Info */}
            <Stack gap={3} bg="bg.panel" color="fg" px={5} py={4} borderRadius="xl" boxShadow="xl">
              <Stack gap={0}>
                <Text textStyle="title" fontWeight="medium">
                  Info – bg.panel (gray.950)
                </Text>
                <Text textStyle="caption" opacity={0.8}>
                  Realtime notifications (WS), clickable
                </Text>
              </Stack>
              <Stack gap={0.5}>
                <Text textStyle="micro">New chat message from Player</Text>
                <Text textStyle="micro">New comment from Player</Text>
                <Text textStyle="micro">You&apos;ve been invited to a session</Text>
                <Text textStyle="micro">Player joined the session</Text>
                <Text textStyle="micro">Player created a session for Game</Text>
                <Text textStyle="micro">Session started / active</Text>
              </Stack>
            </Stack>
          </Grid>

          {/* With Avatar */}
          <Stack gap={2}>
            <Text textStyle="title" color="fg.secondary">
              With Avatar (WS toasts)
            </Text>
            <Text textStyle="caption" color="fg.muted">
              When backend sends avatar in WS event, toast shows user avatar instead of type icon.
              Falls back to initials if no image. No avatar data = standard icon.
            </Text>
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={3}>
              {(
                [
                  {
                    bg: 'bg.panel',
                    color: 'fg',
                    shadow: true,
                    avatar: 'ALEXddddd',
                    title: 'New message from ALEXddddd',
                    desc: 'Hey, wanna play ranked?',
                  },
                  {
                    bg: 'bg.panel',
                    color: 'fg',
                    shadow: true,
                    avatar: 'Player42',
                    title: 'Player42 joined the session',
                  },
                  {
                    bg: 'orange.solid',
                    color: 'orange.contrast',
                    avatar: 'Rival99',
                    title: 'Rival99 left the session',
                  },
                  {
                    bg: 'bg.panel',
                    color: 'fg',
                    shadow: true,
                    title: 'New session available!',
                    desc: 'Open to view and join',
                    note: 'No avatar – standard icon',
                  },
                ] as Array<{
                  bg: string;
                  color: string;
                  shadow?: boolean;
                  avatar?: string;
                  title: string;
                  desc?: string;
                  note?: string;
                }>
              ).map((t) => (
                <HStack
                  key={t.title}
                  gap={3}
                  bg={t.bg}
                  color={t.color}
                  px={4}
                  py={3}
                  borderRadius="l2"
                  boxShadow={t.shadow ? 'xl' : undefined}
                  alignItems="center"
                >
                  {t.avatar ? (
                    <Avatar name={t.avatar} size="xs" flexShrink={0} />
                  ) : (
                    <Circle size="28px" bg="blue.solid" flexShrink={0} />
                  )}
                  <Stack gap={0} flex={1}>
                    <Text textStyle="sm" fontWeight="medium">
                      {t.title}
                    </Text>
                    {t.desc && (
                      <Text textStyle="sm" opacity={0.8}>
                        {t.desc}
                      </Text>
                    )}
                  </Stack>
                </HStack>
              ))}
            </Grid>
          </Stack>

          <Stack gap={2} bg="bg.surface" borderRadius="xl" p={4}>
            <Text textStyle="title" color="fg.secondary">
              Toast Anatomy
            </Text>
            <Text textStyle="caption" color="fg.muted">
              Toast.Root → Avatar (if meta.avatar/avatarName) or Toast.Indicator (icon) + Stack
              (Title + Description) + Toast.CloseTrigger. Loading type shows Spinner. Clickable
              toasts navigate to meta.href on click.
            </Text>
            <HStack gap={4} flexWrap="wrap">
              <HStack gap={2}>
                <Circle size="16px" bg="green.solid" />
                <Text textStyle="micro" color="fg.muted">
                  success: green.solid
                </Text>
              </HStack>
              <HStack gap={2}>
                <Circle size="16px" bg="red.solid" />
                <Text textStyle="micro" color="fg.muted">
                  error: red.solid
                </Text>
              </HStack>
              <HStack gap={2}>
                <Circle size="16px" bg="orange.solid" />
                <Text textStyle="micro" color="fg.muted">
                  warning: orange.solid
                </Text>
              </HStack>
              <HStack gap={2}>
                <Circle size="16px" bg="bg.panel" borderWidth="1px" borderColor="border.subtle" />
                <Text textStyle="micro" color="fg.muted">
                  info: bg.panel
                </Text>
              </HStack>
            </HStack>
          </Stack>
        </SectionTitle>

        {/* ── All Palettes ───────────────────────────────────────── */}
        <SectionTitle title="All Palettes">
          <Stack gap={3}>
            {[
              {
                label: 'Brand (surfaces)',
                palette: 'brand',
                shades: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'],
              },
              {
                label: 'Purple (primary accent)',
                palette: 'purple',
                shades: [
                  '50',
                  '100',
                  '200',
                  '300',
                  '400',
                  '500',
                  '600',
                  '700',
                  '800',
                  '900',
                  '950',
                ],
              },
              {
                label: 'Green (success)',
                palette: 'green',
                shades: [
                  '50',
                  '100',
                  '200',
                  '300',
                  '400',
                  '500',
                  '600',
                  '700',
                  '800',
                  '900',
                  '950',
                ],
              },
              {
                label: 'Teal (links)',
                palette: 'teal',
                shades: [
                  '50',
                  '100',
                  '200',
                  '300',
                  '400',
                  '500',
                  '600',
                  '700',
                  '800',
                  '900',
                  '950',
                ],
              },
              {
                label: 'Blue (info)',
                palette: 'blue',
                shades: [
                  '50',
                  '100',
                  '200',
                  '300',
                  '400',
                  '500',
                  '600',
                  '700',
                  '800',
                  '900',
                  '950',
                ],
              },
              {
                label: 'Red (error)',
                palette: 'red',
                shades: [
                  '50',
                  '100',
                  '200',
                  '300',
                  '400',
                  '500',
                  '600',
                  '700',
                  '800',
                  '900',
                  '950',
                ],
              },
              {
                label: 'Orange (warning)',
                palette: 'orange',
                shades: [
                  '50',
                  '100',
                  '200',
                  '300',
                  '400',
                  '500',
                  '600',
                  '700',
                  '800',
                  '900',
                  '950',
                ],
              },
              {
                label: 'Yellow (accent)',
                palette: 'yellow',
                shades: ['50', '100', '200', '300', '400', '500', '600', '700', '800'],
              },
              {
                label: 'Gray (secondary)',
                palette: 'gray',
                shades: [
                  '50',
                  '100',
                  '200',
                  '300',
                  '400',
                  '500',
                  '600',
                  '700',
                  '800',
                  '900',
                  '950',
                ],
              },
              {
                label: 'Pink (unused)',
                palette: 'pink',
                shades: [
                  '50',
                  '100',
                  '200',
                  '300',
                  '400',
                  '500',
                  '600',
                  '700',
                  '800',
                  '900',
                  '950',
                ],
              },
            ].map((p) => (
              <Stack key={p.palette} gap={1}>
                <Text textStyle="title" color="fg.secondary">
                  {p.label}
                </Text>
                <HStack gap={0} borderRadius="xl" overflow="hidden">
                  {p.shades.map((shade) => (
                    <Box
                      key={shade}
                      flex={1}
                      h="48px"
                      bg={`${p.palette}.${shade}`}
                      display="flex"
                      alignItems="flex-end"
                      justifyContent="center"
                      pb={1}
                    >
                      <Text
                        textStyle="micro"
                        color={
                          Number(shade) > (p.palette === 'brand' ? 300 : 400) ? 'white' : 'black'
                        }
                      >
                        {shade}
                      </Text>
                    </Box>
                  ))}
                </HStack>
              </Stack>
            ))}
          </Stack>
        </SectionTitle>

        {/* ── Dialog header ──────────────────────────────────────── */}
        <SectionTitle title="Dialog Header">
          <Text textStyle="body" color="fg.secondary">
            Одна шапка на все модалки: заголовок subtitle, необязательные иконка и подпись, крестик
            по центру шапки, волосяной разделитель. Заливки нет – цветной остаётся только линия под
            шапкой у модалок со своей айдентикой.
          </Text>
          <HStack gap={3} flexWrap="wrap">
            <DialogPreview label="Plain" title="Delete post" />
            <DialogPreview
              label="Icon + description"
              title="Listing is live!"
              icon
              description="Your listing is visible to other players."
            />
            <DialogPreview label="Branded accent" title="Discord integration" accent />
          </HStack>
        </SectionTitle>

        {/* ── Loaders ────────────────────────────────────────────── */}
        <SectionTitle title="Loaders">
          <Text textStyle="body" color="fg.secondary">
            Правило одно: пока мы знаем форму будущего контента – рисуем скелетон, когда не знаем –
            спиннер с задержкой {DELAY} мс. Спиннер без задержки допустим только там, где действие
            запустил сам пользователь и ждать он готов. Полноэкранных крутилок на месте контента
            быть не должно: они читаются как «зависло».
          </Text>

          {/* Где встречается */}
          <Stack gap={2}>
            <Text textStyle="title" color="fg.secondary">
              Где это встречается в продукте
            </Text>
            <Text textStyle="caption" color="fg.muted">
              Экран → что именно мигает у пользователя → какой паттерн там стоит. Ссылки открывают
              живой экран: чтобы увидеть заглушку, обновите страницу с холодным кэшем.
            </Text>
            <Stack gap={0} borderRadius="lg" borderWidth="1px" borderColor="border.subtle">
              {loaderPlaces.map((place, i) => (
                <Flex
                  key={`${place.screen}-${place.what}`}
                  px={4}
                  py={3}
                  gap={4}
                  alignItems={{ base: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                  flexDirection={{ base: 'column', md: 'row' }}
                  borderTopWidth={i === 0 ? 0 : '1px'}
                  borderColor="border.subtle"
                >
                  <Stack gap={0.5} flex={1} minW={0}>
                    <Link to={place.to} textStyle="title" color="fg">
                      {place.screen}
                    </Link>
                    <Text textStyle="caption" color="fg.muted">
                      {place.what}
                    </Text>
                  </Stack>
                  <Text
                    textStyle="label"
                    color={KIND_LABEL[place.kind].color}
                    flexShrink={0}
                    textAlign={{ base: 'left', md: 'right' }}
                  >
                    {KIND_LABEL[place.kind].text}
                  </Text>
                </Flex>
              ))}
            </Stack>
            <Text textStyle="caption" color="fg.subtle">
              Красным отмечено единственное место, которое стоит починить: на входе через
              Discord/Google форма пропадает и остаётся пустой экран с крутилкой – это самый
              чувствительный шаг воронки.
            </Text>
          </Stack>

          <Stack gap={6}>
            {/* Skeletons */}
            <Stack gap={2}>
              <HStack gap={2}>
                <Text textStyle="title" color="fg.secondary">
                  Skeleton – когда форма контента известна
                </Text>
                <UseBadge count={17} />
              </HStack>
              <Text textStyle="caption" color="fg.muted">
                Основной вариант для списков и карточек: страница не прыгает, потому что заглушка
                занимает то же место, что и данные. Так сделаны чаты, профиль, календарь сессий.
              </Text>
              <HStack gap={6} flexWrap="wrap" alignItems="flex-start">
                <Stack gap={2}>
                  <Text textStyle="caption" color="fg.muted">
                    Список (ChatListSkeleton)
                  </Text>
                  <Box
                    bg="bg.surface"
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor="border.subtle"
                  >
                    <ChatSkeletonPreview />
                  </Box>
                </Stack>
                <Stack gap={2}>
                  <Text textStyle="caption" color="fg.muted">
                    Плитки (ProfileGames)
                  </Text>
                  <HStack gap={3}>
                    {[0, 1].map((i) => (
                      <Skeleton key={i} w="140px" aspectRatio="16 / 9" borderRadius="lg" />
                    ))}
                  </HStack>
                </Stack>
              </HStack>
            </Stack>

            {/* Skeleton as wrapper */}
            <Stack gap={2}>
              <Text textStyle="title" color="fg.secondary">
                Skeleton loading=&#123;&#125; – обёртка вокруг готовой разметки
              </Text>
              <Text textStyle="caption" color="fg.muted">
                Не нужно повторять форму руками: заглушка снимает размер с содержимого и исчезает,
                когда данные пришли. Так сделана панель ссылки-приглашения.
              </Text>
              <WrapSkeletonDemo />
            </Stack>

            {/* Delayed spinner */}
            <Stack gap={2}>
              <HStack gap={2}>
                <Text textStyle="title" color="fg.secondary">
                  Спиннер с задержкой – для подгрузки
                </Text>
                <UseBadge count={12} />
              </HStack>
              <Text textStyle="caption" color="fg.muted">
                Появляется, только если ответ не пришёл за {DELAY} мс. Быстрый запрос проходит без
                вспышки крутилки. Нажмите обе кнопки и сравните.
              </Text>
              <HStack gap={4} flexWrap="wrap" alignItems="flex-start">
                <DelayedSpinnerDemo latency={200} label="Ответ за 200 мс – спиннера нет" />
                <DelayedSpinnerDemo latency={2200} label="Ответ за 2200 мс – спиннер виден" />
              </HStack>
            </Stack>

            {/* Button loading */}
            <Stack gap={2}>
              <HStack gap={2}>
                <Text textStyle="title" color="fg.secondary">
                  Кнопка в состоянии отправки
                </Text>
                <UseBadge count={77} />
              </HStack>
              <Text textStyle="caption" color="fg.muted">
                Самый частый лоудер в проекте. Кнопка сама блокируется и показывает прогресс –
                отдельный спиннер рядом с формой не нужен.
              </Text>
              <HStack gap={3} flexWrap="wrap" alignItems="center">
                <LoadingButtonDemo />
                <Button size="sm" variant="outline" loading loadingText="Сохраняем">
                  С подписью
                </Button>
              </HStack>
            </Stack>

            {/* Sizes */}
            <Stack gap={2}>
              <Text textStyle="title" color="fg.secondary">
                Размеры спиннера
              </Text>
              <HStack gap={6} flexWrap="wrap" alignItems="flex-end">
                {[
                  { size: 'sm' as const, border: '2px', desc: 'В тостах и строках' },
                  { size: 'md' as const, border: '2px', desc: 'Внутри карточки' },
                  { size: 'lg' as const, border: '3px', desc: 'Блок целиком' },
                  { size: 'xl' as const, border: '4px', desc: 'Подгрузка ленты' },
                ].map((item) => (
                  <Stack key={item.size} alignItems="center" gap={2}>
                    <Spinner size={item.size} borderWidth={item.border} animationDuration="0.85s" />
                    <Text textStyle="micro" color="fg.muted">
                      {item.size}
                    </Text>
                    <Text textStyle="micro" color="fg.subtle">
                      {item.desc}
                    </Text>
                  </Stack>
                ))}
              </HStack>
            </Stack>

            {/* Do / Don't */}
            <Stack gap={2}>
              <Text textStyle="title" color="fg.secondary">
                Что выбрать
              </Text>
              <Stack gap={0} borderRadius="lg" borderWidth="1px" borderColor="border.subtle">
                {[
                  { when: 'Список, карточки, профиль', use: 'Skeleton', ok: true },
                  {
                    when: 'Догрузка следующей страницы',
                    use: `Спиннер после ${DELAY} мс`,
                    ok: true,
                  },
                  { when: 'Отправка формы', use: 'Button loading', ok: true },
                  {
                    when: 'Одно поле внутри готовой карточки',
                    use: 'Skeleton loading={}',
                    ok: true,
                  },
                  { when: 'Экран целиком вместо контента', use: 'Не делаем', ok: false },
                  {
                    when: 'Спиннер сразу, без задержки',
                    use: 'Только по клику пользователя',
                    ok: false,
                  },
                ].map((row, i) => (
                  <Flex
                    key={row.when}
                    px={4}
                    py={3}
                    gap={4}
                    alignItems="center"
                    justifyContent="space-between"
                    borderTopWidth={i === 0 ? 0 : '1px'}
                    borderColor="border.subtle"
                  >
                    <Text textStyle="body" color="fg.secondary">
                      {row.when}
                    </Text>
                    <Text
                      textStyle="label"
                      color={row.ok ? 'fg.success' : 'fg.error'}
                      textAlign="right"
                      flexShrink={0}
                    >
                      {row.use}
                    </Text>
                  </Flex>
                ))}
              </Stack>
            </Stack>
          </Stack>
        </SectionTitle>

        {/* ── Spacing ────────────────────────────────────────────── */}
        <SectionTitle title="Spacing Scale">
          <HStack gap={2} flexWrap="wrap" alignItems="flex-end">
            {[1, 2, 3, 4, 5, 6, 8, 10, 12, 16, 20].map((n) => (
              <Stack key={n} alignItems="center" gap={1}>
                <Box w={n} h={n} bg="purple.400" borderRadius="sm" minW="4px" minH="4px" />
                <Text textStyle="micro" color="fg.muted">
                  {n}
                </Text>
              </Stack>
            ))}
          </HStack>
        </SectionTitle>

        {/* Footer */}
        <Text textStyle="caption" color="fg.muted" textAlign="center" py={4}>
          Teamplay Design System · {new Date().toLocaleDateString()}
        </Text>
      </Stack>
    </Box>
  );
};

export const Route = createFileRoute('/{-$locale}/_layout/ui')({
  component: DesignSystemPage,
  head: () => ({
    meta: [{ title: 'Design System | Teamplay.gg' }, { name: 'robots', content: 'noindex' }],
  }),
});
